import { api, extractListItems, extractPaging, extractResponsePaging, type ApiResponse, type PaginationInfo } from '../client';

export interface EmployeeUser {
  uuid: string;
  full_name: string;
  email: string;
  department?: string;
  job_title?: string;
  join_date?: string;
  leave_balance?: number;
  status?: string;  
  location?: string;
  credit_total?: number;
  total_donated_hours?: number;
  total_donation_amount?: number;
  total_pending_hours?: number;
  last_donation_request_date?: string;
  phone?: string;
  user_status?: {
    name: string;
  };
  donation_requests?: {
    uuid: string;
    created_at: string;
    leave_hours: number;
    donation_request_status: {
      name: string;
    };
    charity: {
      name: string;
    };
    employer: {
      uuid: string;
      name: string;
      email: string;
    };
  }[];
}

export interface EmployeeFilters {
  department?: string;
  status?: string;
  phrase?: string;
  sort_by?: 'full_name' | 'email' | 'department' | 'job_title' | 'join_date' | 'leave_balance' | 'status';
  sort_direction?: 'asc' | 'desc';
  page?: number;
  per_page?: number;
}

export interface EmployeeResponse {
  success: boolean;
  data: EmployeeUser[];
  paging?: PaginationInfo;
  message?: string;
}

export interface EmployeeDetailResponse {
  success: boolean;
  data: EmployeeUser;
  message?: string;
}

export interface CreateEmployeeRelationship {
  user_uuid: string;
  employer_uuid: string;
}

export interface EmployeeRelationshipResponse {
  success: boolean;
  data: {
    uuid: string;
    employer: {
      uuid: string;
      name: string;
      email: string;
    };
    user: {
      uuid: string;
      full_name: string;
      email: string;
    };
  };
  message?: string;
}

// API base URL for employer employees
const API_BASE_URL = '/employer/employees';

export class EmployerEmployeeService {
  private static instance: EmployerEmployeeService;

  private constructor() {}

  public static getInstance(): EmployerEmployeeService {
    if (!EmployerEmployeeService.instance) {
      EmployerEmployeeService.instance = new EmployerEmployeeService();
    }
    return EmployerEmployeeService.instance;
  }

  /**
   * Get all employees for the authenticated employer
   * @param filters Optional filters for the request
   * @returns Promise with the employees data
   */
  public async getEmployees(filters?: EmployeeFilters): Promise<EmployeeResponse> {
    try {
      const response = await api.get<EmployeeResponse>(API_BASE_URL, {
        params: filters
      });
      const items = extractListItems<EmployeeUser>(response.data.data);
      const paging = extractResponsePaging(response) ?? extractPaging(response.data.data);
      return { ...response.data, data: items, paging };
    } catch (error) {
      console.error('Error fetching employer employees:', error);
      throw error;
    }
  }

  /**
   * Get a specific employee by UUID
   * @param uuid The UUID of the employee
   * @returns Promise with the employee data
   */
  public async getEmployeeDetail(uuid: string): Promise<EmployeeUser> {
    try {
      const response = await api.get<ApiResponse<EmployeeUser>>(`${API_BASE_URL}/${uuid}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching employee detail:', error);
      throw error;
    }
  }

  public async getEmployeesDetail(uuid: string): Promise<EmployeeUser> {
    try {
      const response = await api.get<ApiResponse<EmployeeUser>>(`/user/${uuid}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching employee detail:', error);
      throw error;
    }
  }

  /**
   * Create a new relationship between employer and employee
   * @param data The employee relationship data
   * @returns Promise with the created relationship
   */
  public async createEmployeeRelationship(data: CreateEmployeeRelationship): Promise<EmployeeRelationshipResponse> {
    try {
      const response = await api.post<EmployeeRelationshipResponse>(
        `${API_BASE_URL}`,
        data
      );
      return response.data;
    } catch (error) {
      console.error('Error creating employee relationship:', error);
      throw error;
    }
  }

  /**
   * Delete an employee relationship
   * @param uuid The UUID of the employee
   * @returns Promise with success message
   */
  public async deleteEmployeeRelationship(uuid: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.delete<{ success: boolean; message: string }>(
        `${API_BASE_URL}/${uuid}`
      );
      return response.data;
    } catch (error) {
      console.error('Error deleting employee relationship:', error);
      throw error;
    }
  }

  /**
   * Update employee details
   * @param uuid The UUID of the employee
   * @param data The updated employee data
   * @returns Promise with the updated employee
   */
  public async updateEmployee(uuid: string, data: Partial<EmployeeUser>): Promise<EmployeeUser> {
    try {
      const response = await api.put<ApiResponse<EmployeeUser>>(
        `${API_BASE_URL}/${uuid}`,
        data
      );
      return response.data.data;
    } catch (error) {
      console.error('Error updating employee:', error);
      throw error;
    }
  }
}

export default EmployerEmployeeService.getInstance(); 